import { useProjectParams } from '@hooks/useProjectParams';
import { RequestStatus } from 'features/request/requestSlice';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { InputMaybe, ProjectMethodologyEnum } from 'gql/graphql';
import { CatalogOtherCostVariableTypeEnum } from 'gql/graphql';
import { getKeyAndItemsFromData, useFetchAllData } from 'hooks/useFetchAllData';
import type { AnyObject } from 'types/AnyObject';
import { methodologyMapReverse } from 'types/methodology';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useCallback, useEffect, useMemo } from 'react';

import type { FormulaEditorObjectSource } from './helpers';
import { VARIABLE_DESERIALIZE_PATTERN, VARIABLE_SHIELD } from './helpers';

export const useEditorParseData = <DataType = any, Variables = any>({
    getObjectSourceQuery,
    queryVariables,
    withEffect = false,
    methodology,
    sourceDataGetter,
    dataObjModifier,
    customSerializer,
}: {
    getObjectSourceQuery?: string | TypedDocumentNode<DataType, Variables>;
    queryVariables?: Variables;
    withEffect?: boolean;
    methodology?: ProjectMethodologyEnum;
    sourceDataGetter?: (item: DataType) => {
        entityName: string;
        entityChild?: string;
    };
    dataObjModifier?: (items: DataType[]) => AnyObject[];
    customSerializer?: (item: string) => string;
}) => {
    const { methodology: methodologyParam } = useProjectParams();

    const projectMethodology = useMemo(() => {
        if (!methodologyParam && !methodology) {
            return undefined;
        }

        return methodology ?? methodologyMapReverse[methodologyParam ?? ''];
    }, [methodology, methodologyParam]);

    const requestVariables = useMemo<
        | Variables
        | {
              skip?: InputMaybe<number>;
              take?: InputMaybe<number>;
              methodology: ProjectMethodologyEnum;
              globalOnly: boolean;
          }
        | undefined
    >(() => {
        if (queryVariables) {
            return queryVariables;
        }

        if (!projectMethodology) {
            return undefined;
        }

        return {
            take: 1000,
            skip: 0,
            methodology: projectMethodology,
            globalOnly: false,
        };
    }, [projectMethodology, queryVariables]);

    const [sourceData, getSourceDataStatus] = useFetchAllData({
        requestId: 'formulaEditorDataSource',
        query: getObjectSourceQuery ?? '',
        variables: requestVariables,
        withEffect: Boolean(withEffect && projectMethodology),
    });

    const [resetFormulaSource] = useResetStoreData({
        requestId: 'formulaEditorDataSource',
    });

    const source = useMemo(() => {
        if (sourceData) {
            return getKeyAndItemsFromData(sourceData, dataObjModifier).items;
        }

        return [];
    }, [dataObjModifier, sourceData]);

    useEffect(
        () => () => {
            resetFormulaSource(undefined);
        },
        [resetFormulaSource],
    );

    const isSourceDataLoading = getSourceDataStatus === RequestStatus.FETCHING;

    const sourceDataForEditor = useMemo(
        () =>
            source?.reduce((acc: FormulaEditorObjectSource, item: AnyObject) => {
                if (sourceDataGetter) {
                    const { entityName, entityChild } = sourceDataGetter(item as DataType);

                    if (acc[entityName]) {
                        if (entityChild) {
                            acc[entityName].push(entityChild);
                        }
                    }

                    acc[entityName] = [];
                } else if ('entityName' in item) {
                    if (acc[item.entityName]) {
                        acc[item.entityName].push(item.name);
                    } else {
                        acc[item.entityName] = [item.name];
                    }
                }

                return acc;
            }, {}) ?? {},
        [source, sourceDataGetter],
    );

    const serializeStringForBackend = useCallback(
        (fieldValue: string) => {
            if (!source) {
                return '';
            }

            return fieldValue
                ?.split(VARIABLE_SHIELD)
                ?.map((item) => {
                    if (VARIABLE_SHIELD.test(item)) {
                        if (customSerializer) {
                            return customSerializer(item.substring(2, item.length - 1));
                        }

                        const objectFromSource = source.find(
                            (obj: AnyObject) => `${obj.entityName}.${obj.name}` === item.slice(2, -1),
                        );

                        if (!objectFromSource) {
                            return item;
                        }

                        let tail = '';

                        if (objectFromSource.type === CatalogOtherCostVariableTypeEnum.Simple) {
                            tail = `${objectFromSource.entityUniqueId}.${objectFromSource.dataSourceId}`;
                        }
                        if (objectFromSource.type === CatalogOtherCostVariableTypeEnum.Composite) {
                            tail = `${objectFromSource.entityUniqueId}.REF:${objectFromSource.uniqueId}`;
                        }
                        if (
                            objectFromSource.type === CatalogOtherCostVariableTypeEnum.OtherCostType ||
                            objectFromSource.type === CatalogOtherCostVariableTypeEnum.LimitedCostType
                        ) {
                            tail = `${objectFromSource.entityUniqueId}.TIP:${objectFromSource.otherTypeUniqueId}`;
                        }

                        return `[@${objectFromSource.entityName}.${objectFromSource.name}|${tail}]`;
                    }

                    return item;
                })
                .join('');
        },
        [customSerializer, source],
    );

    const deserializeFromBackend = useCallback((formula: string | undefined | null) => {
        if (!formula) {
            return '';
        }

        return formula?.replace(VARIABLE_DESERIALIZE_PATTERN, '[@$1]');
    }, []);

    return { isSourceDataLoading, sourceDataForEditor, serializeStringForBackend, deserializeFromBackend };
};
